<template>
    <ui-section title="Publicações DJEN" :description="description">
        <template #actions>
            <span v-if="unread" class="djp-new-count">{{ unread }} nova(s)</span>
            <v-btn v-if="canEdit && unread" outlined small color="primary" class="mr-2" :loading="busy === 'all'" @click="readAll"><v-icon left small>mdi-check-all</v-icon>Marcar todas como cientes</v-btn>
            <v-btn depressed small color="primary" :loading="syncing" @click="syncNow"><v-icon left small>mdi-cloud-sync-outline</v-icon>Consultar DJEN</v-btn>
        </template>
        <ui-empty v-if="!loading && !items.length" icon="mdi-newspaper-variant-outline" text="Nenhuma publicação capturada para este processo." />

        <article v-for="item in items" :key="item.id" class="djp-item" :class="{ 'djp-item--unread': !item.read_at }">
            <div class="djp-item__head">
                <span v-if="!item.read_at" class="djp-dot" title="Nova"></span>
                <div class="djp-item__main">
                    <div class="djp-item__title">{{ item.communication_type || 'Publicação' }} · {{ formatDate(item.available_on) }} <span v-if="!item.read_at" class="djp-new">Nova</span></div>
                    <div class="djp-muted">
                        {{ [item.tribunal, item.organ].filter(Boolean).join(' · ') }}
                        <span v-if="item.process_number_masked"> · {{ item.process_number_masked }}</span>
                    </div>
                </div>
                <span v-if="item.read_at" class="djp-muted">Ciente em {{ formatDateTime(item.read_at) }}<span v-if="item.read_by_name"> por {{ item.read_by_name }}</span></span>
            </div>
            <div class="djen-people">
                <div>
                    <div class="djen-people__label">Destinatários</div>
                    <div v-for="(party, index) in mainParties(item)" :key="'p' + index" class="djen-people__row">{{ party.name }} <span class="djen-people__muted">{{ poleName(party.pole) }}</span></div>
                    <div v-if="item.parties.length > mainParties(item).length" class="djen-people__muted">+{{ item.parties.length - mainParties(item).length }} intimados</div>
                </div>
                <div v-if="item.lawyers.length">
                    <div class="djen-people__label">Advogados intimados</div>
                    <div v-for="(lawyer, index) in item.lawyers" :key="'l' + index" class="djen-people__row">
                        {{ lawyer.name }} <span v-if="lawyer.oab" class="djen-people__muted">OAB {{ lawyer.state }} {{ lawyer.oab }}</span>
                        <span v-if="lawyer.procurator" class="djen-chip djen-chip--ok" :title="lawyer.procurator.name"><v-icon x-small color="#15803d">mdi-account-check-outline</v-icon> Procurador cadastrado · {{ lawyer.procurator.creditors }} credor(es)</span>
                        <span v-else class="djen-chip">Não cadastrado</span>
                    </div>
                </div>
            </div>
            <div class="djp-text" :class="{ 'djp-text--open': expanded === item.id }">{{ item.text }}</div>
            <div class="djp-actions">
                <v-btn text x-small color="primary" @click="expanded = expanded === item.id ? null : item.id">{{ expanded === item.id ? 'Recolher texto' : 'Ver texto completo' }}</v-btn>
                <v-btn v-if="item.link" text x-small color="primary" :href="item.link" target="_blank" rel="noopener"><v-icon left x-small>mdi-open-in-new</v-icon>Abrir no tribunal</v-btn>
                <v-spacer></v-spacer>
                <template v-if="canEdit">
                    <v-btn small outlined color="primary" class="mr-2" @click="openRegister(item)">
                        <v-icon left small>{{ module === 'other_proceeding' ? 'mdi-timeline-plus-outline' : 'mdi-comment-plus-outline' }}</v-icon>
                        {{ module === 'other_proceeding' ? 'Registrar movimentação' : 'Registrar como comentário' }}
                    </v-btn>
                    <v-btn small text color="grey darken-1" :loading="busy === item.id" @click="toggleRead(item)">{{ item.read_at ? 'Desfazer ciente' : 'Marcar como ciente' }}</v-btn>
                </template>
            </div>
        </article>

        <v-dialog v-model="dialog.show" max-width="620" persistent>
            <v-card v-if="dialog.item">
                <v-card-title>{{ module === 'other_proceeding' ? 'Registrar movimentação' : 'Registrar como comentário' }}</v-card-title>
                <v-card-text>
                    <template v-if="module === 'other_proceeding'">
                        <v-row dense>
                            <v-col cols="12" md="6"><v-text-field v-model="dialog.movement_type" label="Tipo da movimentação *" outlined dense hide-details="auto" /></v-col>
                            <v-col cols="6" md="3"><v-text-field v-model="dialog.occurred_at" type="date" label="Data" outlined dense hide-details="auto" /></v-col>
                            <v-col cols="6" md="3"><v-text-field v-model="dialog.due_at" type="date" label="Prazo" outlined dense hide-details="auto" /></v-col>
                        </v-row>
                    </template>
                    <v-textarea v-model="dialog.text" :label="module === 'other_proceeding' ? 'Descrição' : 'Comentário'" rows="8" outlined hide-details="auto" class="mt-3" />
                    <p class="djp-muted mt-2 mb-0">A publicação fica marcada como ciente.</p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text :disabled="dialog.saving" @click="dialog.show = false">Cancelar</v-btn>
                    <v-btn color="primary" depressed :loading="dialog.saving" :disabled="module === 'other_proceeding' && !dialog.movement_type" @click="confirm">Registrar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </ui-section>
</template>

<script>
import axios from 'axios';
import { displayDate } from '@/common/util/date-time';

// Publicacoes do DJEN de um processo cadastrado (Impugnacao ou Outros processos).
export default {
    name: "DjenPublications",

    props: {
        module: { type: String, required: true }, // proceeding | other_proceeding
        recordId: { type: [Number, String], required: true },
        canEdit: { type: Boolean, default: true },
    },

    data: () => ({
        loading: false,
        items: [],
        unread: 0,
        checkedAt: null,
        expanded: null,
        busy: null,
        syncing: false,
        dialog: { show: false, item: null },
    }),

    computed: {
        base() {
            return this.module === 'proceeding' ? `/api/admin/proceedings/${this.recordId}/djen` : `/api/admin/other-proceedings/${this.recordId}/djen`;
        },
        description() {
            const checked = this.checkedAt ? ` Última consulta deste processo: ${this.formatDateTime(this.checkedAt)}.` : '';
            return `Publicações do Diário de Justiça Eletrônico Nacional capturadas pelo número do processo.${checked}`;
        },
    },

    watch: {
        recordId() {
            this.load();
        },
    },

    created() {
        this.load();
    },

    methods: {
        formatDate(value) {
            return value ? this.$moment(String(value).slice(0, 10)).format('DD/MM/YYYY') : '';
        },

        formatDateTime(value) {
            return value ? displayDate(value).format('DD/MM/YYYY HH:mm') : '';
        },

        async load() {
            this.loading = true;
            try {
                const result = await axios.get(this.base);
                this.items = result.data.content.data;
                this.unread = result.data.content.unread;
                this.checkedAt = result.data.content.checked_at;
                this.$emit('loaded', { total: this.items.length, unread: this.unread });
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.loading = false;
            }
        },

        poleName(pole) {
            return ({ A: 'Polo ativo', P: 'Polo passivo', T: 'Terceiro' })[pole] || '';
        },

        // Autor e reu; os intimados em massa (terceiros) so entram no "+N".
        mainParties(item) {
            const main = (item.parties || []).filter((party) => party.pole === 'A' || party.pole === 'P');
            return (main.length ? main : item.parties || []).slice(0, 6);
        },

        async readAll() {
            this.busy = 'all';
            try {
                await axios.post(`${this.base}/read-all`);
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.busy = null;
            }
        },

        // Busca agora no DJEN as publicacoes deste processo.
        async syncNow() {
            this.syncing = true;
            try {
                const result = await axios.post(`${this.base}/sync`);
                const content = result.data.content;
                this.items = content.data;
                this.unread = content.unread;
                this.checkedAt = content.checked_at;
                this.$emit('loaded', { total: this.items.length, unread: this.unread });
                this.$dialog.notify.success(content.created ? `${content.created} publicação(ões) nova(s) no DJEN.` : 'Nenhuma publicação nova no DJEN.', { position: 'top-right', timeout: 4000 });
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 6000 });
            } finally {
                this.syncing = false;
            }
        },

        async toggleRead(item) {
            this.busy = item.id;
            try {
                await axios.post(`${this.base}/${item.id}/read`, { read: !item.read_at });
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.busy = null;
            }
        },

        openRegister(item) {
            this.dialog = {
                show: true, item, saving: false,
                movement_type: `${item.communication_type || 'Publicação'} (DJEN)`,
                occurred_at: item.available_on ? String(item.available_on).slice(0, 10) : '',
                due_at: '',
                text: this.summary(item),
            };
        },

        // Resumo para a movimentacao/comentario; a integra fica na aba do DJEN.
        summary(item) {
            const where = [item.tribunal, item.organ].filter(Boolean).join(' · ');
            const text = String(item.text || '').replace(/\s+/g, ' ').trim();
            const excerpt = text.length > 500 ? `${text.slice(0, 500)}…` : text;
            return `${item.communication_type || 'Publicação'} publicada no DJEN em ${this.formatDate(item.available_on)}${where ? ` (${where})` : ''}.\n\n${excerpt}`;
        },

        async confirm() {
            const d = this.dialog;
            d.saving = true;
            try {
                if (this.module === 'other_proceeding') {
                    await axios.post(`${this.base}/${d.item.id}/movement`, { movement_type: d.movement_type, occurred_at: d.occurred_at, due_at: d.due_at || null, description: d.text });
                    this.$dialog.notify.success('Movimentação registrada.', { position: 'top-right', timeout: 3000 });
                } else {
                    await axios.post(`${this.base}/${d.item.id}/comment`, { content: d.text });
                    this.$dialog.notify.success('Comentário registrado.', { position: 'top-right', timeout: 3000 });
                }
                this.dialog.show = false;
                await this.load();
                this.$emit('changed');
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 6000 });
            } finally {
                d.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.djp-item { padding: 12px 14px; border: 1px solid #e4e7ec; border-radius: 10px; margin-bottom: 10px; background: #fff; }
.djp-item--unread { border-color: #fcd34d; border-left: 4px solid #f59e0b; background: #fffbeb; }
.djp-new { display: inline-block; margin-left: 6px; padding: 0 8px; border-radius: 999px; background: #fef3c7; color: #b45309; font-size: 11px; font-weight: 700; line-height: 18px; }
.djp-new-count { margin-right: 10px; padding: 2px 10px; border-radius: 999px; background: #fef3c7; color: #b45309; font-size: 12px; font-weight: 700; }
.djen-people { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 6px 24px; margin-top: 10px; font-size: 13px; color: #344054; }
.djen-people__label { font-size: 11px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: #667085; margin-bottom: 2px; }
.djen-people__row { line-height: 1.7; }
.djen-people__muted { font-size: 12px; color: #667085; }
.djen-chip { display: inline-flex; align-items: center; gap: 3px; margin-left: 6px; padding: 0 8px; border-radius: 999px; background: #f2f4f7; color: #475467; font-size: 11px; font-weight: 600; line-height: 18px; }
.djen-chip--ok { background: #dcfce7; color: #15803d; }
.djp-item__head { display: flex; align-items: flex-start; gap: 10px; flex-wrap: wrap; }
.djp-item__main { flex: 1 1 360px; min-width: 0; }
.djp-item__title { font-size: 14px; font-weight: 600; color: #101828; }
.djp-dot { width: 8px; height: 8px; border-radius: 50%; background: #f59e0b; margin-top: 6px; }
.djp-muted { font-size: 12.5px; color: #667085; }
.djp-text { margin-top: 8px; padding: 10px 12px; border-radius: 8px; background: #f9fafb; font-size: 12.5px; color: #344054; white-space: pre-wrap; max-height: 64px; overflow: hidden; }
.djp-text--open { max-height: none; }
.djp-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 4px; margin-top: 8px; }
</style>
